import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";
import { cutLongTextJs, handleEnterVms } from "utils";
import { AppPath, UserType } from "utils/constants";
import useBoolean from "hooks/useBoolean";
import Modal from "components/modal";
import ChangePasswordModal from "parts/Account/ChangePasswordModal";
import { formatDateJsFromTimeStamp } from "utils";
import { useCommonStore } from "stores/common-store";
import { BellSVG } from "assets/images";
import { JA } from "utils/strings.ja";

import { NotificationService } from "context/notification/NotificationService";

const Header = ({ sidebarToggle, handleSidebarToggle }) => {
  const navigate = useNavigate();
  const { loginData, logout } = useAuth();
  const [showLogoutModal, openLogoutModal, closeLogoutModal] = useBoolean();
  const [isEnteringVms, setIsEnteringVms] = React.useState(false);
  const [showAccountMenu, setShowAccountMenu] = React.useState(false);
  const accountMenuRef = useRef(null);
  const [
    showChangePasswordModal,
    openChangePasswordModal,
    closeChangePasswordModal,
  ] = useBoolean();

  // close the account menu on a click outside it or on Escape
  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const onPointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) setShowAccountMenu(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowAccountMenu(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showAccountMenu]);

  // Publish the header's height as --app-header-h, so panels pinned under it
  // (the notification panel) start exactly at its bottom edge whatever its
  // content makes it.
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const publish = () =>
      document.documentElement.style.setProperty(
        "--app-header-h",
        `${el.offsetHeight}px`
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const {
      locations,
      showNotificationPanel,
      toggleShowNotificationPanel,
      anyNotificationUnread,
      toggleShowNotificationUnread,
      setNotificationData,
    } = useCommonStore((state) => state);

  useEffect(async () => {
    await checkNoti();
  }, []);

  let locationName = "";
  if (loginData.type === UserType.Location) {
    locationName = locations[0]?.descName;
  }

  const handleVmsClick = async () => {
    setIsEnteringVms(true);
    try {
      await handleEnterVms(loginData.tenantID, locationName);
    } catch (error) {
      toast(JA.vms.accessFailed, { type: "error" });
    } finally {
      setIsEnteringVms(false);
    }
  };

  async function handleLogout() {
    try {
      await logout();
      navigate(AppPath.Login);
    } catch (error) {
      console.log("error signing out: ", error);
    }
  }

  const checkNoti = async () => {
    // check noti when load app
    const serviceNotification = new NotificationService(loginData);
    const listNotis = await serviceNotification.getListNotification();
    
    // Store notification data for reuse
    setNotificationData(listNotis);
    
    // Check if any notification is unread and return early
    const hasUnread = listNotis.items.some((item) => {
      const readBy = item.readBy || [];
      return !readBy.includes(loginData.username);
    });

    toggleShowNotificationUnread(hasUnread);
  };

  return (
    <>
      <header ref={headerRef} className="sticky top-0 flex w-full bg-white z-99 drop-shadow-1 dark:bg-boxdark dark:drop-shadow-none">
        <div className="flex items-center justify-between grow px-4 py-4 shadow-2 md:px-6">
          <div className="flex items-center gap-2 sm:gap-4 lg:hidden">
            {/* Hamburger Toggle BTN */}
            <button
              className="z-99999 block rounded-xs border border-stroke bg-white p-1.5 shadow-xs dark:border-strokedark dark:bg-boxdark lg:hidden"
              onClick={handleSidebarToggle}
            >
              <span className="relative block h-5.5 w-5.5 cursor-pointer">
                <span className="absolute right-0 w-full h-full du-block">
                  <span
                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-xs bg-black delay-[0] duration-200 ease-in-out dark:bg-white ${
                      !sidebarToggle ? "w-full! delay-300" : ""
                    }`}
                  ></span>
                  <span
                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-xs bg-black delay-150 duration-200 ease-in-out dark:bg-white ${
                      !sidebarToggle ? "w-full! delay-400" : ""
                    }`}
                  ></span>
                  <span
                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-xs bg-black delay-200 duration-200 ease-in-out dark:bg-white ${
                      !sidebarToggle ? "w-full! delay-500" : ""
                    }`}
                  ></span>
                </span>
                <span className="absolute right-0 w-full h-full rotate-45 du-block">
                  <span
                    className={`absolute left-2.5 top-0 block h-full w-0.5 rounded-xs bg-black delay-300 duration-200 ease-in-out dark:bg-white ${
                      !sidebarToggle ? "h-0! delay-[0]" : ""
                    }`}
                  ></span>
                  <span
                    className={`delay-400 absolute left-0 top-2.5 block h-0.5 w-full rounded-xs bg-black duration-200 ease-in-out dark:bg-white ${
                      !sidebarToggle ? "h-0! dealy-200" : ""
                    }`}
                  ></span>
                </span>
              </span>
            </button>
            {/* Hamburger Toggle BTN */}
          </div>
          {/* Icon header */}
          <div className="flex justify-end w-full">
            <div className="flex items-center gap-3 2xsm:gap-7">
              <ul className="flex items-center gap-2 2xsm:gap-4">
                {/*  */}
                <li className="relative">
                  <button
                    // the panel's click-outside handler leaves this button to toggle it
                    data-notification-toggle
                    aria-label={JA.notification.title}
                    className="text-black relative flex h-8.5 rounded-md px-2 items-center justify-center bg-slate-100 hover:text-black dark:border-strokedark dark:bg-meta-4 dark:text-white"
                    onClick={() => {
                      toggleShowNotificationPanel(!showNotificationPanel);
                      // if (anyNotificationUnread) {
                      //   toggleShowNotificationUnread(false);
                      // }
                    }}
                  >
                    <BellSVG hasNew={anyNotificationUnread} />
                  </button>
                </li>
                {/*  */}
                {/* account menu: the user's own profile and password */}
                <li className="relative" ref={accountMenuRef}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={showAccountMenu}
                    aria-label={JA.account.menu}
                    className="flex items-center gap-2 rounded-md px-2 py-1 text-left text-black hover:bg-slate-100 dark:hover:bg-meta-4"
                    onClick={() => setShowAccountMenu((open) => !open)}
                  >
                    <span>
                      <span className="block font-medium dark:text-white">
                        Hello, {cutLongTextJs(loginData.username, 20)}
                      </span>
                      <span className="block text-xs dark:text-bodydark1">
                        {JA.auth.lastLogin}{" "}
                        {loginData.last_login
                          ? formatDateJsFromTimeStamp(loginData.last_login)
                          : JA.auth.firstLogin}
                      </span>
                    </span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true"
                      className={`shrink-0 transition-transform ${showAccountMenu ? "rotate-180" : ""}`}>
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {showAccountMenu && (
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-lg border border-stroke bg-white py-1 shadow-default dark:border-strokedark dark:bg-boxdark"
                    >
                      {/* UserProfile (アカウント情報) is disabled for now — the page
                          redirects to login — so it has no entry here yet. */}
                      <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-black hover:bg-brand-soft dark:text-white dark:hover:bg-meta-4"
                        onClick={() => {
                          setShowAccountMenu(false);
                          openChangePasswordModal();
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="4" y="11" width="16" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                        </svg>
                        {JA.password.change}
                      </button>
                    </div>
                  )}
                </li>
                {[UserType.Location, UserType.Tenant].includes(
                  loginData.type
                ) && (
                  <li className="relative">
                    <button
                      className="flex justify-center px-3 py-2 font-semibold rounded-sm bg-slate-100 text-brand hover:text-black hover:bg-slate-100/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isEnteringVms}
                      onClick={handleVmsClick}
                    >
                      {JA.vms.access}
                    </button>
                  </li>
                )}
                <li className="relative">
                  <button
                    type="button"
                    className="flex h-8.5 items-center gap-1.5 rounded-md border border-stroke bg-white px-3 text-sm font-medium text-black transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-strokedark dark:bg-meta-4 dark:text-white dark:hover:text-red-400"
                    onClick={() => openLogoutModal()}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <path d="M16 17l5-5-5-5" />
                      <path d="M21 12H9" />
                    </svg>
                    {JA.auth.logout}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          {/* End Icon header */}
        </div>
      </header>
      {/* Modal logout */}
      {showLogoutModal && (
        <Modal
          title={JA.auth.logoutConfirm}
          isOpen={showLogoutModal}
          handleCloseModal={closeLogoutModal}
          primaryButton={JA.auth.logout}
          primaryAction={handleLogout}
          error={""}
        >
          <div>{JA.auth.exitConfirm}</div>
        </Modal>
      )}
      {/* End Modal logout */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          handleCloseModal={closeChangePasswordModal}
        />
      )}
    </>
  );
};
export default Header;
